import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {
    ALL_TAGS,
    ALL_TEAMS,
    CATEGORY_LABELS,
    FeedbackCategory,
    FeedbackFilters as Filters,
    FeedbackStatus,
    FeedbackTag,
    FeedbackTeam,
    STATUS_LABELS,
    TAG_LABELS,
    TEAM_LABELS,
    PLATFORM_DISPLAY_NAMES,
    AppPlatform,
} from '../types/feedback';
import {useFilterOptions} from '../hooks/useFeedback';

interface FeedbackFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
}

export function FeedbackFilters({ filters, onFilterChange, onClearFilters }: FeedbackFiltersProps) {
    const t = useTraduire();
    const {platforms, assignees, versions, loading} = useFilterOptions();

  const hasActiveFilters = Object.values(filters).some(
    (v) => v !== undefined && v !== ''
  );

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex flex-wrap gap-4">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.rechercheLabel)}
          </label>
          <input
            type="text"
            value={filters.searchQuery || ''}
            onChange={(e) => onFilterChange({ searchQuery: e.target.value || undefined })}
            placeholder={t(T.rechercher)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          />
        </div>

        {/* Status */}
        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.colonneEtat)}
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) =>
              onFilterChange({
                status: (e.target.value as FeedbackStatus) || undefined,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          >
            <option value="">{t(T.tous)}</option>
            {Object.entries(STATUS_LABELS).map(([value, libelle]) => (
              <option key={value} value={value}>
                {t(libelle)}
              </option>
            ))}
          </select>
        </div>

        {/* Category */}
        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.categorie)}
          </label>
          <select
            value={filters.category || ''}
            onChange={(e) =>
              onFilterChange({
                category: (e.target.value as FeedbackCategory) || undefined,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          >
            <option value="">{t(T.tous)}</option>
            {Object.entries(CATEGORY_LABELS).map(([value, libelle]) => (
              <option key={value} value={value}>
                {t(libelle)}
              </option>
            ))}
          </select>
        </div>

          {/* Team */}
          <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t(T.colonneEquipe)}
              </label>
              <select
                  value={filters.team || ''}
                  onChange={(e) =>
                      onFilterChange({
                          team: (e.target.value as FeedbackTeam) || undefined,
                      })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              >
                  <option value="">{t(T.tous)}</option>
                  {ALL_TEAMS.map((team) => (
                      <option key={team} value={team}>
                          {t(TEAM_LABELS[team])}
                      </option>
                  ))}
              </select>
          </div>

          {/* Tag */}
          <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t(T.etiquette)}
              </label>
              <select
                  value={filters.tag || ''}
                  onChange={(e) =>
                      onFilterChange({
                          tag: (e.target.value as FeedbackTag) || undefined,
                      })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              >
                  <option value="">{t(T.tous)}</option>
                  {ALL_TAGS.map((tag) => (
                      <option key={tag} value={tag}>
                          {t(TAG_LABELS[tag])}
                      </option>
                  ))}
              </select>
          </div>

        {/* Platform */}
        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.plateforme)}
          </label>
          <select
            value={filters.platform || ''}
            onChange={(e) =>
              onFilterChange({ platform: e.target.value || undefined })
            }
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          >
            <option value="">{t(T.tous)}</option>
            {platforms.map((platform) => (
              <option key={platform} value={platform}>
                {PLATFORM_DISPLAY_NAMES[platform as AppPlatform] || platform}
              </option>
            ))}
          </select>
        </div>

          {/* Version filter (>= selected version) */}
          <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version ≥
              </label>
              <select
                  value={filters.fromVersion || ''}
                  onChange={(e) =>
                      onFilterChange({fromVersion: e.target.value || undefined})
                  }
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              >
                  <option value="">{t(T.tous)}</option>
                  {versions.map((version) => (
                      <option key={version} value={version}>
                          {version}
                      </option>
                  ))}
              </select>
          </div>

        {/* Beta Only */}
        <div className="w-32">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.modeBeta)}
          </label>
          <select
            value={filters.betaEnabled === undefined ? '' : String(filters.betaEnabled)}
            onChange={(e) =>
              onFilterChange({
                betaEnabled:
                  e.target.value === '' ? undefined : e.target.value === 'true',
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          >
            <option value="">{t(T.tous)}</option>
            <option value="true">{t(T.beta)}</option>
            <option value="false">{t(T.stable)}</option>
          </select>
        </div>

        {/* Assignee */}
        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.responsable)}
          </label>
          <select
            value={filters.assignee || ''}
            onChange={(e) =>
              onFilterChange({ assignee: e.target.value || undefined })
            }
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          >
            <option value="">{t(T.tous)}</option>
            {assignees.map((assignee) => (
              <option key={assignee} value={assignee}>
                {assignee}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range */}
        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.dateDebut)}
          </label>
          <input
            type="date"
            value={filters.startDate?.toISOString().split('T')[0] || ''}
            onChange={(e) =>
              onFilterChange({
                startDate: e.target.value ? new Date(e.target.value) : undefined,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          />
        </div>

        <div className="w-40">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t(T.dateFin)}
          </label>
          <input
            type="date"
            value={filters.endDate?.toISOString().split('T')[0] || ''}
            onChange={(e) =>
              onFilterChange({
                endDate: e.target.value ? new Date(e.target.value) : undefined,
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
          />
        </div>
      </div>

        {/* Active UID filter badge */}
        {filters.uid && (
        <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{t(T.filtreParUtilisateur)}</span>
                <span
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <span className="font-mono text-xs">{filters.uid.slice(0, 12)}...</span>
              <button
                  onClick={() => onFilterChange({uid: undefined})}
                  className="ml-1 text-primary hover:text-primary-dark"
                  title={t(T.retirerLeFiltre)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </span>
            </div>
        </div>
        )}

        {/* Quick filter: unread replies */}
        <div className="w-full flex items-center gap-4">
          <button
            onClick={() => onFilterChange({ hasUnreadUserReply: filters.hasUnreadUserReply ? undefined : true })}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-colors ${
              filters.hasUnreadUserReply
                ? 'bg-red-50 border-red-300 text-red-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${filters.hasUnreadUserReply ? 'bg-red-500' : 'bg-gray-300'}`}/>
            {t(T.reponsesNonLues)}
          </button>
        </div>

        {hasActiveFilters && (
            <div className={`mt-4 pt-4 border-t border-gray-200 ${filters.uid ? 'mt-2 pt-2 border-t-0' : ''}`}>
          <button
            onClick={onClearFilters}
            className="text-sm text-primary hover:text-primary-dark"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}
